"use client"
import { subscribeBackInStock } from "@/actions/customer/subscribe-back-in-stock"
import { keepSelection } from "@/actions/selection/keep-selection"
import IconClose from "@/app/(site)/_components/Atoms/Icons/IconClose"
import IconNotification from "@/app/(site)/_components/Atoms/Icons/IconNotification"
import StoryText from "@/app/(site)/_components/Atoms/Storytext"
import Text from "@/app/(site)/_components/Atoms/Text"
import ButtonPill from "@/app/(site)/_components/Molecules/ButtonPill"
import { Spinner } from "@/app/(site)/_components/Molecules/Spinner"
import InputField from "@/app/(site)/_components/Molecules/TextField"
import { useAnimatedModule } from "@/app/(site)/_utils/_hooks/useAnimatedModule"
import { useDocumentKeydown } from "@/app/(site)/_utils/_hooks/useDocumentKeydown"
import { useDocumentOverflowControl } from "@/app/(site)/_utils/_hooks/useDocumentOverflowControl"
import { useEscClose } from "@/app/(site)/_utils/_hooks/useEscClose"
import { getLangCode } from "@/app/(site)/_utils/misc"
import useEcommerceStore from "@/app/(site)/contexts/ecommerce/store"
import { useAppSelector } from "@/app/(site)/contexts/Global"
import { ProductData } from "@/lib/content-service/types/ContentServiceProduct"
import { t } from "@/translations"
import {
    CheckoutAPIProduct,
    RelatedProduct
} from "@/types/centra/checkout-api/products/post-products"
import { Err, Ok } from "@/types/generic/Result"
import placeholder from "@assets/images/placeholder.png"
import { useQuery } from "@tanstack/react-query"
import Image from "next/image"
import { usePathname } from "next/navigation"
import React, { FormEvent } from "react"
import { toast } from "react-hot-toast"
import { match } from "ts-pattern"

type NotifyMeModalProps = {
    product: CheckoutAPIProduct | RelatedProduct | ProductData
}

export default function NotifyMeModal({ product }: NotifyMeModalProps) {
    const [isAnimating, setIsAnimating] = React.useState(false)
    const [email, setEmail] = React.useState("")
    const [loading, setLoading] = React.useState(false)

    const [success, setSuccess] = React.useState<undefined | boolean>(undefined)
    const setNotifyMeModalOpen = useEcommerceStore(state => state.toggleNotifyModal)
    const isNotifyMeModalOpen = useEcommerceStore(state => state.isNotifyMeModalOpen)
    const state = useAppSelector(state => state.product)
    const selectedSizeName = state.selectedSize

    const langCode = getLangCode(usePathname().split("/")[1]) ?? ""

    const selectedSize =
        state?.availableSizes.find(size => size.isSelected) ||
        state?.availableSizes[0] ||
        {}

    const item = selectedSize?.item || ""

    // Function to toggle body scroll
    const toggleBodyScroll = (isModalOpen: boolean) => {}

    const { data } = useQuery({
        queryKey: ["selection"],
        queryFn: () => keepSelection()
    })
    const handleModuleClose = () => {
        // Start closing animation
        setIsAnimating(false)

        setTimeout(() => {
            setNotifyMeModalOpen(false)
        }, 300) // Adjust the duration to match your CSS animation
    }
    useAnimatedModule(isNotifyMeModalOpen, setIsAnimating)
    useDocumentOverflowControl(isNotifyMeModalOpen) // Controls the overflow of the body based on the modal state
    useEscClose(isNotifyMeModalOpen, handleModuleClose) // Closes the modal on Escape key
    useDocumentKeydown({
        keys: ["Escape"], // Listens for Escape and Backspace keys
        action: handleModuleClose, // Calls handleModuleClose on key press
        condition: isNotifyMeModalOpen // Only active when the modal is shown
    })

    // Reset the success state
    React.useEffect(() => {
        if (success !== undefined) {
            const timer = setTimeout(() => {
                setSuccess(undefined)
            }, 3000)

            return () => clearTimeout(timer)
        }
    }, [success])

    React.useEffect(() => {
        // Disable scroll when component mounts
        toggleBodyScroll(isNotifyMeModalOpen)

        // Enable scroll when component unmounts
        return () => toggleBodyScroll(false)
    }, [isNotifyMeModalOpen])

    if (!isNotifyMeModalOpen) return null

    const handleNotifyMeSubmit = async (
        e: FormEvent<HTMLFormElement>,
        productId: string,
        userEmail: string
    ) => {
        e.preventDefault()
        setLoading(true)
        if (data) {
            const cart = match(data)
                .with(Ok, value => value.data)
                .with(Err, () => {
                    toast.error("Failed to retrieve cart data")
                })
                .exhaustive()

            try {
                const res: any = await subscribeBackInStock({
                    email: userEmail,
                    country: cart?.checkoutApiSelection?.location?.country || "US",
                    language:
                        cart?.checkoutApiSelection?.location?.language?.language || "EN",
                    item: item
                })

                if (res.token) {
                    setSuccess(true)
                    toast.success("Success", {
                        duration: 5000,
                        position: "bottom-left"
                    })
                    setEmail("") // Clear the form field
                } else {
                    throw new Error("Failed to subscribe") // Handle the case where `res.token` might be undefined or false
                }
            } catch (error) {
                setSuccess(false)

                // If the email is not valid
                const emailRegex = new RegExp(
                    // eslint-disable-next-line no-useless-escape
                    /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/
                )
                if (!emailRegex.test(userEmail)) {
                    toast.error(t(langCode, "invalid email format"), {
                        duration: 4000,
                        position: "bottom-right"
                    })
                } else {
                    toast.error("Something went wrong, please try again", {
                        duration: 4000,
                        position: "bottom-right"
                    })
                }
            }
            setLoading(false) // Reset submitting state
        }
    }
    // Conditional classes based on animation state
    const modalClasses = isAnimating ? "translate-x-0" : "translate-x-full" // Slide in from right
    const blurClasses = isAnimating ? "opacity-100" : "opacity-100"

    return (
        <div>
            <div
                className={`fixed top-0 z-30 h-full w-[100%] bg-black/20 backdrop-blur-md transition-all duration-300 ease-in-out ${blurClasses}`}
                onClick={() => handleModuleClose()}
                style={{ opacity: isAnimating ? 1 : 0 }}
            />

            <div
                className={`fixed right-0 top-0 z-50 h-full bg-white px-24 py-32 transition-transform duration-300 ${modalClasses}`}
            >
                <div className="flex h-full w-full flex-col justify-between md:w-550">
                    <div>
                        <div className="mb-48 flex items-center justify-between">
                            <Text size="medium" weight="regular" className="col-span-5">
                                {t(langCode, "back-in-stock-subscribe-title")}
                            </Text>
                            <div
                                className="col-span-1 flex cursor-pointer justify-end"
                                onClick={() => setNotifyMeModalOpen(false)}
                            >
                                <IconClose />
                            </div>
                        </div>
                        <div className="flex w-full gap-16">
                            <div className="w-104">
                                <Image
                                    src={
                                        product?.media?.standard
                                            ? (product as ProductData)?.media
                                                  ?.standard?.[0].url
                                            : (product as CheckoutAPIProduct)
                                                  ?.mediaObjects?.[0]?.sources
                                                  ?.standard?.[0]?.url ?? placeholder
                                    }
                                    alt={product.name}
                                    width={500}
                                    height={500}
                                />
                            </div>
                            <div className="flex flex-col justify-between">
                                <div>
                                    <Text size="medium" weight="regular">
                                        {product.name}
                                    </Text>
                                    <Text
                                        size="medium"
                                        weight="regular"
                                        className="text-main-dark-grey"
                                    >
                                        {product.color_swatch?.color_name}
                                    </Text>
                                    <Text
                                        size="medium"
                                        weight="regular"
                                        className="text-main-dark-grey"
                                    >
                                        {selectedSizeName}
                                    </Text>
                                </div>
                                <Text size="medium" weight="regular">
                                    {product.price}
                                </Text>
                            </div>
                        </div>
                    </div>
                    <div className="flex grow flex-col justify-end">
                        <StoryText size="size3" className="mb-29">
                            {t(langCode, "back-in-stock-subscribe-description")}
                        </StoryText>
                        <div>
                            <form
                                className="mb-24 flex flex-col gap-16 text-stone-400"
                                onSubmit={e =>
                                    handleNotifyMeSubmit(e, product.items[0].item, email)
                                }
                            >
                                <InputField
                                    placeholder={t(langCode, "adam.smith@email.com")}
                                    id="id"
                                    label={t(langCode, "email")}
                                    theme="light"
                                    single="true"
                                    value={email}
                                    onChange={e => setEmail(e.target.value)}
                                    success={success}
                                />
                                <ButtonPill
                                    aria-label={t(langCode, "Notify me")}
                                    color="black"
                                    size="large"
                                    variant="primary"
                                    fullWidth
                                >
                                    {loading ? (
                                        <Spinner classes="w-30 h-30" /> // Show spinner when loading
                                    ) : (
                                        <>
                                            {t(langCode, "Notify me")}
                                            <IconNotification
                                                aria-hidden="true"
                                                className="ml-8 h-16 w-16"
                                            />
                                        </>
                                    )}
                                    {/* Create a tailwind spinning icon if loading is true */}
                                </ButtonPill>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
